/**
 * Routing.
 *
 * The whole app sits behind the sign-in check: there is no signed-out state worth
 * rendering except sign-in itself, and a half-rendered shell while auth resolves
 * is how a teacher ends up looking at an empty paper list that is not empty.
 */

import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import type { ReactNode } from 'react'
import { Shell } from './Shell'
import { Home } from '../features/home/Home'
import { SignIn } from '../features/auth/SignIn'
import { Papers } from '../features/papers/Papers'
import { Builder } from '../features/builder/Builder'
import { Pictures } from '../features/pictures/Pictures'
import { Review } from '../features/queues/Review'
import { Printing } from '../features/queues/Printing'
import { Settings } from '../features/school/Settings'
import { Schools } from '../features/platform/Schools'
import { SchoolControl } from '../features/platform/SchoolControl'
import { SiteSettings } from '../features/platform/SiteSettings'
import { MockCheckout } from '../features/billing/MockCheckout'
import { AuditLog } from '../features/audit/AuditLog'
import { Spinner, Toaster } from '../components/ui'
import { AuthProvider } from './auth'
import { ErrorBoundary } from './ErrorBoundary'
import { useAuth } from './useAuth'

function Routed() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="tc-boot">
        <Spinner label="Loading TeachCraftAI" />
      </div>
    )
  }

  if (!user) return <SignIn />

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route path="/home" element={<Home />} />
        <Route path="/schools" element={<ForRoles roles={['admin']}><Schools /></ForRoles>} />
        <Route
          path="/schools/:schoolId/settings"
          element={<ForRoles roles={['admin']}><SchoolControl /></ForRoles>}
        />
        <Route
          path="/site-settings"
          element={<ForRoles roles={['admin']}><SiteSettings /></ForRoles>}
        />
        <Route
          path="/audit"
          element={<ForRoles roles={['admin', 'school_admin']}><AuditLog /></ForRoles>}
        />
        <Route path="/papers" element={<ForRoles roles={['teacher']}><Papers /></ForRoles>} />
        <Route path="/papers/:id" element={<ForRoles roles={['teacher']}><Builder /></ForRoles>} />
        <Route path="/pictures" element={<ForRoles roles={['teacher']}><Pictures /></ForRoles>} />
        <Route path="/review" element={<ForRoles roles={['reviewer']}><Review /></ForRoles>} />
        <Route path="/review/:id" element={<ForRoles roles={['reviewer']}><Review /></ForRoles>} />
        <Route
          path="/printing"
          element={<ForRoles roles={['print_admin', 'school_admin']}><Printing /></ForRoles>}
        />
        <Route
          path="/settings"
          element={<ForExactRoles roles={['school_admin']}><Settings /></ForExactRoles>}
        />
        <Route
          path="/billing/mock"
          element={<ForRoles roles={['school_admin']}><MockCheckout /></ForRoles>}
        />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Route>
    </Routes>
  )
}

function ForRoles({ roles, children }: { roles: string[]; children: ReactNode }) {
  const { user } = useAuth()
  if (!user?.roles.includes('admin') && !roles.some((role) => user?.roles.includes(role))) {
    return <Navigate to="/home" replace />
  }
  return children
}

function ForExactRoles({ roles, children }: { roles: string[]; children: ReactNode }) {
  const { user } = useAuth()
  if (!roles.some((role) => user?.roles.includes(role))) {
    return <Navigate to="/home" replace />
  }
  return children
}

export function App() {
  return (
    <BrowserRouter basename="/app">
      <ErrorBoundary>
        <AuthProvider>
          <Routed />
          <Toaster />
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  )
}
